import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { TableShell } from '../../components/molecules/TableShell'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import {
  useRegisteredCustomers,
  type RegisteredCustomerDto
} from '../../../viewmodels/useRegisteredCustomers'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /customers/registered — list of registered customers, with a
 * permission-gated **Block / Unblock** action per row.
 *
 * Data comes from /iam/customers (iam_users where user_type_code='customer').
 */
export function RegisteredCustomersPage(): JSX.Element {
  const {
    items,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    setStatus
  } = useRegisteredCustomers()
  const [actionError, setActionError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const handleToggle = async (row: RegisteredCustomerDto): Promise<void> => {
    const next = row.status === 'active' ? 'suspended' : 'active'
    const verb = next === 'suspended' ? 'block' : 'unblock'
    if (
      typeof window !== 'undefined' &&
      !window.confirm(`Are you sure you want to ${verb} ${row.email}?`)
    ) {
      return
    }
    setBusyId(row.id)
    setActionError(null)
    const res = await setStatus(row.id, next)
    setBusyId(null)
    if (!res.ok) setActionError(res.message)
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Registered Customers</h1>
      </header>

      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {actionError !== null ? <Alert tone="error">{actionError}</Alert> : null}

      {isLoading && items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table items={items} onToggle={(row) => void handleToggle(row)} busyId={busyId} />
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No customers yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        New rows land here when shoppers register through the storefront.
      </p>
    </div>
  )
}

function Table({
  items,
  onToggle,
  busyId
}: {
  readonly items: ReadonlyArray<RegisteredCustomerDto>
  readonly onToggle: (row: RegisteredCustomerDto) => void
  readonly busyId: string | null
}): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.CUSTOMERSREGISTERED, 'edit')
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Marketing</Th>
            <Th>Status</Th>
            <Th>Registered</Th>
            <Th>Last login</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} className="border-t border-border">
              <Td>{formatName(row.first_name, row.last_name)}</Td>
              <Td className="font-mono">{row.email}</Td>
              <Td className="font-mono whitespace-nowrap">{row.phone ?? '—'}</Td>
              <Td>
                {row.accepts_marketing ? (
                  <span className="text-forest text-body-xs font-medium">Opted in</span>
                ) : (
                  <span className="text-text-secondary text-body-xs">—</span>
                )}
              </Td>
              <Td>
                <StatusBadge status={row.status} />
              </Td>
              <Td className="text-text-secondary whitespace-nowrap">{formatDate(row.created_at)}</Td>
              <Td className="text-text-secondary whitespace-nowrap">
                {row.last_login_at !== null ? formatDate(row.last_login_at) : '—'}
              </Td>
              <Td className="text-right whitespace-nowrap">
                {row.status === 'deleted' || !canEdit ? (
                  <span className="text-text-secondary text-body-xs">—</span>
                ) : (
                  <Button
                    size="sm"
                    variant={row.status === 'active' ? 'ghost' : 'secondary'}
                    onClick={() => onToggle(row)}
                    disabled={busyId === row.id}
                  >
                    {busyId === row.id
                      ? 'Saving…'
                      : row.status === 'active'
                        ? 'Block'
                        : 'Unblock'}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}

function StatusBadge({ status }: { readonly status: RegisteredCustomerDto['status'] }): JSX.Element {
  const tone =
    status === 'active'
      ? 'bg-forest/15 text-forest'
      : status === 'suspended' || status === 'deleted'
        ? 'bg-terra/15 text-terra'
        : 'bg-bamboo/20 text-charcoal'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium ${tone}`}>
      {status}
    </span>
  )
}

function formatName(first: string | null, last: string | null): string {
  const parts = [first, last].filter((p) => p !== null && p.trim() !== '')
  return parts.length === 0 ? '—' : parts.join(' ')
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
